<template>
    <div class="user-list">
        <heading :title="title" tag="user-list-title">
            <b-button
                v-if="$user.isAdmin() || $user.isSuperAdmin()"
                @click="showNotificationModal"
                variant="primary"
                class="ml-3">
                Notify Selected Users
            </b-button>
            <export-button v-if="canCreate"
                           :fields="exportFields"
                           profile-key="user"
                           @export="exportList"/>
        </heading>
        <data-table ref="dataTable"
                    :show-filters="true"
                    :show-pager="true"
                    :get-items="getUsers"
                    v-model="filters"
                    :fields="fields"
                    :selectable-rows="true"
                    @row-selected="rowSelected"
                    @select-item-selected="selectFilterChanged"
                    @multiselect-item-selected="multiselectFilterChanged">
            <!-- template for custom columns -->
            <template v-slot:customField="slotProps">
                <template v-if="slotProps.row.field.key === 'last_name'">
                    <user-details :user="slotProps.row.item"/>
                </template>
                <template v-if="slotProps.row.field.key === 'agency_names'">
                    {{ getNames(slotProps.row.item.agency_names) }}
                </template>
                <template v-else-if="slotProps.row.field.key === 'company_names'">
                    {{ getNames(slotProps.row.item.company_names) }}
                </template>
                <template v-else-if="slotProps.row.field.key === 'status'">
                    {{ getStatusLabel(slotProps.row.item.status) }}
                </template>
                <template v-else-if="slotProps.row.field.key === 'role'">
                    <user-role-badge :user="slotProps.row.item"/>
                </template>
                <template v-else-if="slotProps.row.field.key === 'approved'">
                    <b-badge :variant="slotProps.row.item.approved ? 'success' : 'danger'">
                        {{ slotProps.row.item.approved ? 'True' : 'False' }}
                    </b-badge>
                </template>
                <div v-else-if="slotProps.row.field.key === 'actions'" class="text-nowrap">
                    <b-button title="View User" size="sm" variant="secondary" class="mr-2"
                              :to="`/user/view/${slotProps.row.item.id}`">
                        <b-icon-eye/>
                    </b-button>
                    <b-button v-if="canMask"
                              :disabled="slotProps.row.item.id === userId"
                              title="Assume Identity"
                              size="sm" variant="secondary"
                              @click="maskAsUser(slotProps.row.item.id, slotProps.row.item.first_name, slotProps.row.item.last_name)">
                        <b-icon-emoji-sunglasses/>
                    </b-button>
                </div>
                <div v-else-if="slotProps.row.field.key === 'email'">
                    {{ slotProps.row.item.email }}
                </div>
            </template>
        </data-table>

        <bulk-notification-modal modalId="bulk-notification-modal" :users="selectedUsers"/>
    </div>
</template>

<script>
import Vue from 'vue'
import User from '@/models/User'
import Authorization from '@/providers/Authorization'
import * as _ from 'lodash'
import Export from '@/providers/Export'
import Api from '@/providers/Api'
import UserDetails from '@/components/UserDetails'
import BulkNotificationModal from "@/components/BulkNotificationModal";

export default Vue.extend({
    name: 'UserList',
    components: {BulkNotificationModal, UserDetails },
    props: {
        // support different filters - e.g. /user/list/company/7 OR /user/list/agency/2
        relation: {
            type: String, // 'agency'/'company'
            default: null
        },
        id: {
            type: Number,
            default: null
        }
    },
    data () {
        return {
            pageTitle: 'User List',
            userFields: User,
            exportFields: {
                email: 'Email',
                first_name: 'First Name',
                last_name: 'Last Name',
                phone_office: 'Office Phone',
                phone_mobile: 'Mobile Phone',
                agency_names: 'Agency',
                company_names: 'Company',
                is_superadmin: 'Is Super Admin',
                is_admin: 'Is Application Admin',
                is_auditor: 'Is Auditor',
                role: "Role",
                disabled: 'Disabled'
            },
            canCreate: false,
            canMask: false,
            userId: null,
            filterCompanyName: null,
            filterAgencyName: null,
            selectedUsers: [],
        }
    },
    computed: {
        title () {
            const userCompanyName = this.$user.getCompanyName()
            let title = 'User List'
            if (this.filterCompanyName) {
                title += `: ${this.filterCompanyName}`
            } else if (userCompanyName) {
                title += `: ${userCompanyName}`
            }
            if (this.filterAgencyName) {
                title += `: ${this.filterAgencyName}`
            }
            return title
        },
        fields () {
            let fields = [
                { key: 'last_name', sortable: true, label: 'Name' },
                { key: 'role', sortable: true, label: 'Role' },
            ]
            if (!this.companyId) {
                fields.push({ key: 'agency_names', label: 'Agency' })
            }
            fields = fields.concat([
                { key: 'company_names', label: 'Company' },
                { key: 'last_name', sortable: true, label: 'Last Name' },
                { key: 'email', sortable: true, label: 'Email' },
                { key: 'approved', sortable: false, label: 'Approved' },
                { key: 'actions', sortable: false }
            ])
            return fields
        },
        companyId () {
            if (this.relation === 'company') {
                return this.id
            } else {
                return null
            }
        },
        agencyId () {
            if (this.relation === 'agency') {
                return this.id
            } else {
                return null
            }
        },
        userCompanyId () {
            // this is non-null if user is equipment owner
            return this.$user.getCompanyId()
        },
        userAgencyId () {
            // this is non-null if user is prog admin, project manager, owners rep
            return this.$user.getAgencyId()
        },
        filters: {
            get () {
                const filters = {
                    email: { field: 'email', type: 'string', value: null, label: 'Email' },
                    first_name: { field: 'first_name', type: 'string', value: null, label: 'First Name' },
                    last_name: { field: 'last_name', type: 'string', value: null, label: 'Last Name' },
                }
                if (this.roleFilter) {
                    filters.role = this.roleFilter
                }
                if (this.agencyFilter) {
                    filters.agency_id = this.agencyFilter
                }
                if (this.companyFilter) {
                    filters.company_id = this.companyFilter
                }
                return filters
            },
            set (val) {}
        },
        agencyFilter () {
            if (this.userCompanyId || this.companyId || this.$user.isProjectManager()) {
                return null
            } else {
                return {
                    field: 'agency_id',
                    type: 'select',
                    label: 'Agency',
                    apiTable: 'agency',
                    labelField: 'name',
                    defaultValue: this.agencyId, // from props
                }
            }
        },
        companyFilter () {
            if (this.userCompanyId) { // equipment owner
                return {
                    field: 'company_id',
                    type: 'hidden',
                    value: this.userCompanyId, // from user
                }
            } else {
                return {
                    field: 'company_id',
                    type: 'multiselect',
                    search: (query) => {
                        return Api.getItems(this, 'company', { name: { field: 'name', value: query } })
                    },
                    label: 'Company',
                    apiTable: 'company',
                    labelField: 'name',
                    defaultValue: this.companyId // from props
                }
            }
        },
        roleFilter () {
            if (this.$user.isAdmin() || this.$user.isSuperAdmin()) {
                const roleLabels = User.getRoleLabels()
                return {
                    field: 'role',
                    type: 'select',
                    options: _.map(_.keys(roleLabels), (key) => {
                        return {value: key, text: roleLabels[key]}
                    }),
                    label: 'Role',
                }
            } else {
                return null
            }
        },
    },
    methods: {
        getUsers (listParams) {
            return this.$api.getItems(this, 'user', this.filters, listParams, ['agency_names', 'company_names'])
        },
        maskAsUser (userId, firstName, lastName) {
            this.$bvModal.msgBoxConfirm(firstName && lastName ?
                'Would you like to mask as ' + firstName + ' ' + lastName + '?' :
                'Would you like to mask as this user?')
                .then(async value => {
                    if (value) {
                        this.$user.maskAsUser(userId)
                    }
                })
        },
        getName (item) {
            return item ? item.name : ''
        },
        getNames (value) {
            return value ? _.truncate(_.map(value, (el) => this.getName(el)).join(', ')) : null
        },
        getStatusLabel (value) {
            return User.getStatusLabel(value)
        },
        getRoleLabel (value) {
            return User.getRoleLabel(value)
        },
        selectFilterChanged (field, item) {
            if (field === 'agency_id') {
                this.filterAgencyName = item ? item.text : null
            }
        },
        multiselectFilterChanged (field, item) {
            if (field === 'company_id') {
                this.filterCompanyName = item ? item.name : null
            }
        },
        rowSelected(rows) {
            this.selectedUsers = rows
        },
        showNotificationModal() {
            this.$bvModal.show('bulk-notification-modal')
        },
        async exportList (filetype, fields) {
            //  context
            const contextTable = this.$refs.dataTable.getContext()
            const context = contextTable ? {sortBy: contextTable.sortBy, perPage: 100000, currentPage: 1} : null
            this.$api.getItems(this, 'user', this.filters, context)
                .then((users) => {
                    let callback = (field, value, record) => {
                        switch (field) {
                            case 'status':
                                value = this.getStatusLabel(value)
                                break
                            case 'agency_names':
                                value = this.getNames(record.agencies)
                                break
                            case 'company_names':
                                value = this.getNames(record.companies)
                                break
                            case 'is_superadmin':
                            case 'is_admin':
                            case 'is_auditor':
                                value = value ? 'yes' : null
                                break
                            case 'disabled':
                                value = value ? 'disabled' : 'enabled'
                                break
                            case 'role':
                                value = this.getRoleLabel(value)
                                break
                        }
                        return value
                    }
                    //  export
                    Export.exportRecords(users, fields, 'Users', filetype, callback)
                })
        }
    },
    created () {
        //  authorizations
        Authorization.can({ action: 'create', model: 'user' })
            .then((can) => this.canCreate = can)
        Authorization.can({ action: 'mask', model: 'user' })
            .then((can) => this.canMask = can)
        //  myself
        this.userId = this.$user.userId()
    }
})
</script>

<style lang="scss">
    .user-list .page-search {
        @media (min-width: 576px) and (max-width: 991px) {
            .col {
                width: 48%;
                flex-basis: auto;
            }
        }
    }
</style>